import React, { FC, useEffect, useRef, useState } from 'react';
import { pickBy, find } from 'lodash';
import { FastField, Formik, FormikProps, FormikValues, useFormikContext } from 'formik';
import { Discount, FormModel } from '../../../../models/form/form';
import { StepDefinition } from '../steps/FormSteps';
import useFormSummary from '../summary/useFormSummary';
import { formatToString, formatToStringMultiple } from './fieldToString';
import { PaymentElement as SportsPayPaymentElement } from '../../../form/public/steps/kreezee_V1.0';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useIntl } from 'react-intl';
import EmailField from '../controls/email/EmailField';
import { noop } from '../../../../utilities/FunctionUtilities';
import * as Yup from 'yup';
import FieldWrapper from '../../../library/inputs/fieldWrapper/FieldWrapper';
import SingleMode from './payment-modes/SingleMode';
import FixedDatesMode from './payment-modes/FixedDatesMode';
import DiscountCode from './discount-code/DiscountCode';
import { getText } from '../../../../utilities/CultureUtilities';
import PaymentMethodTiles from './PaymentMethodTiles';
import SandboxIframe from './SandboxIframe';

interface Props {
  form: FormModel;
  organizationName: string;
  currentStep: StepDefinition;
  customerEmail: (string) => void;
  cardHolderName: (string) => void;
  isOtherPayment: (boolean) => void;
  paymentMode: string | undefined;
  onPaymentModeChange: (string) => void;
  discount: Discount;
  onDiscountChange: (Discount) => void;
}

interface Values {
  email: string;
}

const getPaymentModeText = (mode, total, selectedMode) => {
  switch (mode.mode) {
    case 'single':
      return <SingleMode mode={mode} total={total} selected={mode.id == selectedMode}></SingleMode>;
    case 'fixed_dates':
      return <FixedDatesMode mode={mode} total={total} selected={mode.id == selectedMode}></FixedDatesMode>;
  }

  return null;
};

const PaymentSection: FC<Props> = ({
  form,
  organizationName,
  currentStep,
  customerEmail,
  cardHolderName,
  isOtherPayment,
  paymentMode,
  onPaymentModeChange,
  discount,
  onDiscountChange,
}: Props) => {
  const [otherPayment, setOtherPayment] = useState(false);
  const { total } = useFormSummary(form, discount);
  const { values } = useFormikContext<FormikValues>();
  const intl = useIntl();
  const formRef = useRef<FormikProps<Values>>(null);

  const FormEmailErrorMessage = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'formPublic.postSubmission.missingEmail' }))
      .required(intl.formatMessage({ id: 'formPublic.postSubmission.missingEmail' })),
    name: Yup.string().required(intl.formatMessage({ id: 'formPublic.postSubmission.missingName' })),
  });

  useEffect(() => {
    customerEmail('');
    cardHolderName('');
  }, []);

  const emailField = () => {
    return (
      <div className="d-flex justify-content-center align-items-center email p-0 mb-3">
        <Formik
          validationSchema={FormEmailErrorMessage}
          initialValues={{
            email: '',
            name: '',
          }}
          onSubmit={noop}
          innerRef={formRef}
          validateOnChange
          validateOnBlur
        >
          <div className="d-flex flex-column align-items-stretch" style={{ width: '100%' }}>
            <div className="mb-3">
              <FastField name="email">
                {({ field, meta }) => (
                  <FieldWrapper
                    hiddenText={false}
                    textColor={undefined}
                    required={true}
                    label={intl.formatMessage({ id: 'formPublic.receiptEmail' })}
                    visible={true}
                    size={12}
                    error={meta.error}
                    touched={meta.touched}
                  >
                    <EmailField
                      id="email"
                      enabled
                      value={values.email}
                      handleBlur={(e) => {
                        field.onBlur(e);
                        meta.touched = true;
                        if (meta.touched && meta.errors == undefined) {
                          customerEmail(e.target.value);
                        }
                      }}
                      handleChange={(e) => {
                        field.onChange(e);
                        meta.touched = true;
                        if (meta.touched && meta.errors == undefined) {
                          customerEmail(e.target.value);
                        }
                      }}
                      placeholder={undefined}
                      error={meta.error}
                      touched={meta.touched}
                    />
                  </FieldWrapper>
                )}
              </FastField>
            </div>
            {form.paymentEnabled && !otherPayment && total > 0 && (
              <div>
                <FastField name="name">
                  {({ field, meta }) => (
                    <FieldWrapper
                      hiddenText={false}
                      textColor={undefined}
                      required={true}
                      label={intl.formatMessage({ id: 'formPublic.cardHolderName' })}
                      visible={true}
                      size={12}
                      error={meta.error}
                      touched={meta.touched}
                    >
                      <EmailField
                        id="name"
                        enabled
                        value={values.name}
                        handleBlur={(e) => {
                          field.onBlur(e);
                          meta.touched = true;
                          if (meta.touched && meta.errors == undefined) {
                            cardHolderName(e.target.value);
                          }
                        }}
                        handleChange={(e) => {
                          field.onChange(e);
                          meta.touched = true;
                          if (meta.touched && meta.errors == undefined) {
                            cardHolderName(e.target.value);
                          }
                        }}
                        placeholder={undefined}
                        error={meta.error}
                        touched={meta.touched}
                      />
                    </FieldWrapper>
                  )}
                </FastField>
              </div>
            )}
          </div>
        </Formik>
      </div>
    );
  };

  return (
    <div className="payment-section">
      {currentStep.type == 'summary-step' && (
        <div className="mb-3">
          <span className="summary-title">{intl.formatMessage({ id: 'formPublic.summary' })}</span>
          {form.content.steps.map((step) => {
            return (
              <div key={`summary-step-${step.id}`}>
                <div className="step">
                  <div className="step-name">
                    <span>{getText(step.name, intl.locale)}</span>
                  </div>
                </div>
                {step.sections.map((section) => {
                  return (
                    <div style={{ marginBottom: 35 }} key={`summary-section-${section.id}`}>
                      <div className="section">
                        <div className="section-name">
                          <span>{getText(section.name, intl.locale)}</span>
                        </div>
                      </div>
                      {section.multiple && (
                        <div>
                          {(() => {
                            const value = formatToStringMultiple(
                              section,
                              pickBy(values, (_, key) => find(section.fields, { id: key.split('@', 2)[0] })),
                              intl.locale,
                            );

                            return (
                              <div className="field-container" key={`summary-section-multiple-${section.id}`}>
                                <div className="field-value">{value}</div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {!section.multiple && (
                        <div>
                          {section.fields.map((field) => {
                            const value = formatToString(field, values[field.id], intl.locale);
                            if (value === false) return null;
                            return (
                              <div className="field-container" key={`summary-field-${field.id}`}>
                                <div className="field-title">{getText(field.text, intl.locale)}</div>
                                <div className="field-value">{value}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      <div
        className="mb-5 w-100"
        style={{
          display: currentStep.type == 'summary-step' ? 'block' : 'none',
        }}
      >
        {form.paymentEnabled && (
          <div>
            {form.hasDiscountCodes && (
              <DiscountCode
                className="mb-5"
                formId={form.id}
                organizationId={form.organizationId}
                discount={discount}
                onApplyDiscount={(discount) => onDiscountChange(discount)}
              ></DiscountCode>
            )}
            <div className="total-container">
              <div className="total d-flex align-items-center">
                <div className="label">TOTAL</div>
                <div className="amount">{(total / 100).toFixed(2)} $</div>
              </div>
            </div>
            {currentStep.type == 'summary-step' && form.paymentOptional && (
              <>
                <PaymentMethodTiles
                  otherPayment={otherPayment}
                  methods={form.paymentOptionalMethods}
                  onChange={(isOther: boolean) => {
                    setOtherPayment(isOther);
                    isOtherPayment(isOther);
                  }}
                />
                {form.paymentOptionalDescription && (
                  <div className="mb-4">
                    <SandboxIframe
                      visible={otherPayment}
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                      html={getText(form.paymentOptionalDescription, intl.locale)}
                    />
                  </div>
                )}
              </>
            )}
            {!otherPayment &&
              total > 0 &&
              form.paymentModes &&
              form.paymentModes.modes &&
              form.paymentModes.modes.length > 0 && (
                <div className="form-group">
                  <label className="input-label form-label">
                    {intl.formatMessage({ id: 'formPublic.paymentPlan' })}
                  </label>
                  <div className="d-flex flex-row" style={{ marginBottom: 20 }}>
                    {form.paymentModes.modes.map((mode) => {
                      const component = getPaymentModeText(mode, total, paymentMode);
                      return (
                        <div
                          onClick={() => onPaymentModeChange(mode.id)}
                          className="d-flex justify-content-stretch"
                          key={mode.id}
                        >
                          {component}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            {emailField()}
            {!otherPayment && total > 0 && form.paymentAccount?.length != 8 && <PaymentElement />}
            {!otherPayment && total > 0 && form.paymentAccount?.length == 8 && (
              <SportsPayPaymentElement organizationName={organizationName} />
            )}
          </div>
        )}
        {!form.paymentEnabled && emailField()}
      </div>
    </div>
  );
};

export default PaymentSection;
